import { lazy, Suspense } from 'react'
import * as DialogPrimitive from '@radix-ui/react-dialog'
import { DialogOverlay } from '../shared/components/ui/dialog'
import type { UserRole } from './navigation'
import { SearchIcon } from 'lucide-react'

/* Lazy: MainLayout is imported eagerly by the router while every page is
   code-split, so anything the palette imports (jobs/clients/teams queries)
   would otherwise land in the bundle that blocks first paint. The chunk is
   precached by the service worker, so first open still works offline. */
const SearchPaletteBody = lazy(() => import('./SearchPaletteBody'))

interface SearchPaletteProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  /** Namespaces the recents list; recents are skipped when absent */
  userId?: string
  role: UserRole | null
  /** Opens MainLayout's sync dialog — surfaced as a palette command */
  onOpenSyncStatus: () => void
}

/**
 * Global search, as a command palette. Opened from the sidebar's search row
 * (desktop), the nav drawer's search row (mobile), or ⌘K / Ctrl-K anywhere —
 * MainLayout owns the open state and the shortcut.
 *
 * Searches jobs by title/address (server search, cached-job fallback when
 * offline) and clients by name/contact, alongside recently opened items and —
 * on desktop — navigation and action commands.
 */
export function SearchPalette({
  open,
  onOpenChange,
  userId,
  role,
  onOpenSyncStatus,
}: SearchPaletteProps) {
  return (
    <DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
      <DialogPrimitive.Portal>
        <DialogOverlay />
        {/* Top-anchored rather than centred: the results grow downward from a
            fixed input position instead of the whole panel re-centring on
            every keystroke. Higher on phones to clear the keyboard. */}
        <DialogPrimitive.Content className="fixed left-1/2 top-[8vh] z-50 w-[calc(100%-2rem)] max-w-xl -translate-x-1/2 overflow-hidden rounded-[1.75rem] bg-popover text-popover-foreground shadow-lg duration-200 high-contrast:border-2 data-[state=closed]:animate-out data-[state=open]:animate-in data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 md:top-[12vh]">
          <DialogPrimitive.Title className="sr-only">Search</DialogPrimitive.Title>
          <DialogPrimitive.Description className="sr-only">
            Search your jobs and clients, then press Enter to open a result.
          </DialogPrimitive.Description>
          <Suspense fallback={<PaletteSkeleton />}>
            <SearchPaletteBody
              onClose={() => onOpenChange(false)}
              userId={userId}
              role={role}
              onOpenSyncStatus={onOpenSyncStatus}
            />
          </Suspense>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  )
}

/* Matches the real input row exactly, so the panel doesn't resize when the
   chunk lands */
function PaletteSkeleton() {
  return (
    <div className="relative h-16 border-b border-[color-mix(in_srgb,currentColor_10%,transparent)]">
      <SearchIcon className="absolute left-5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
      <span className="absolute left-12 top-1/2 -translate-y-1/2 text-base text-muted-foreground">
        Search jobs and clients…
      </span>
    </div>
  )
}
